<template lang="pug">
  .filters-box(
    :key="`filters-box-value_${filterOption.id}`"
    :title="filterOption.text"
    :class="{ checked }"
  )
    .color(v-if="filterType.includes('color')")
      span.image-swatch(
        v-if="['camo','gold','silver', 'stainless steel'].indexOf(filterOption.text.toLowerCase()) !== -1"
        :style="{ backgroundImage: `url(${imageSwatch})` }"
      )
      span.color-swatch(
        v-else
        :class="`background-color-${filterOption.text.replace(/[ ,]/g, '-').toLowerCase()}`"
      )
      span {{ filterOption.text }}
      Icon.check( v-show="checked" name="check" fill="black" )
    .text(v-else)
      span.text-swatch(:class="{ checked }")
      span.text-display(:class="{ checked }") {{ filterOption.text }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import camo from '@/assets/swatches/camo.jpg'
import gold from '@/assets/swatches/gold.jpg'
import silver from '@/assets/swatches/silver.jpg'
import stainless from '@/assets/swatches/stainlesssteel.jpg'

const props = defineProps({
  filterOption: {
    type    : Object,
    default : () => ({}),
  },

  filterType: {
    type: String,
  },

  checked: {
    type    : Boolean,
    default : false,
  },
})

/**
 * Get the image path (preloaded) for non numeric
 * filter option text value
 * @return {String|null}
 */

const imageSwatch = computed(() => {
  const color = props.filterOption.text.toLowerCase()

  if (color === 'gold') {
    return gold
  }
  if (color === 'camo') {
    return camo
  }
  if (color === 'stainless steel') {
    return stainless
  }

  return silver
})
</script>

<style lang="scss" scoped>
  .filters-box {
    font-size       : $size-xs;
    text-align      : center;
    width           : 100%;
    border          : $border-width-thin solid $color-gray-light;
    cursor          : pointer;
    display         : flex;
    align-items     : center;
    justify-content : center;
    transition      : border $duration-quick ease-in;
    position        : relative;
    overflow        : hidden;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border: $border-width-medium solid white;
      z-index: 1;
    }

    &:after {
      content : '';
      display : block;
      padding-bottom : 100%;
    }

    &:hover {
      border-color : $color-gray-dark;
    }

    &.checked {
      border-color: $color-black;
    }
  }

  .image-swatch, .color-swatch, .text-swatch {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-repeat: no-repeat;
  }

  .text-swatch {
    &.checked {
      background-color: black;
      color: white;
    }
  }

  .text-display {
    position: relative;
    z-index: 2;
    &.checked {
      color: white;
    }
  }

  .check {
    position  : absolute;
    left      : 50%;
    top       : 50%;
    transform : translate(-50%, -50%);

    :deep(svg) {
      stroke: $color-white;
      stroke-width: $border-width-medium;
    }
  }

  // Swatch colors not in SISU
  .background-color-{
    $blue: #1277B7;
    $red: #EA0000;
    $green: #5BBD15;
    $black: #000;
    $white: #fff;
    $olive-green: #4B5320;

    $threeColorStop1: 38%;
    $threeColorStop2: 62%;

    &black {background-color: $black}
    &white {background-color: $white}
    &blue {background-color: $blue}
    &green {background-color: $green}
    &red {background-color: $red}
    &tan {background-color: #e9ddc0}
    &orange {background-color: #ff8901}
    &pink {background-color: #ff3984}
    &purple {background-color: #8e3ea1}
    &olive-green {background-color: $olive-green}
    &yellow {background-color: #fcdb00}
    &brown {background-color: #835d3f}

    &black-olive-green {
      background-image: linear-gradient(-45deg, $olive-green 50%, $black 50%);
    }

    &blue-red-white {
      background-image:
        linear-gradient(-45deg, $white $threeColorStop1, transparent $threeColorStop1),
        linear-gradient(-45deg, $red $threeColorStop2, $blue $threeColorStop2);
    }

    &black-blue-red {
      background-image:
        linear-gradient(-45deg, $red $threeColorStop1, transparent $threeColorStop1),
        linear-gradient(-45deg, $blue $threeColorStop2, $black $threeColorStop2);
    }

    &black-green-red {
      background-image:
        linear-gradient(-45deg, $red $threeColorStop1, transparent $threeColorStop1),
        linear-gradient(-45deg, $green $threeColorStop2, $black $threeColorStop2);
    }
  }
</style>
